import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardActionArea, CardContent, Box, Typography, Chip, IconButton,
  Rating,
} from '@mui/material';
import { Star, StarBorder, AccessTime, People } from '@mui/icons-material';
import { CATEGORIES, CATEGORY_GRADIENTS, DIFFICULTY } from '../../config';
import { updateRecipe } from '../../utils/firebase';

export default function RecipeCard({ recipe }) {
  const navigate = useNavigate();

  const category = CATEGORIES.find((c) => c.value === recipe.category);
  const difficulty = DIFFICULTY.find((d) => d.value === recipe.difficulty);
  const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);
  const gradient = CATEGORY_GRADIENTS[recipe.category] || CATEGORY_GRADIENTS.dinner;

  const handleStarToggle = async (e) => {
    e.stopPropagation();
    try {
      await updateRecipe(recipe.id, { starred: !recipe.starred });
    } catch (err) {
      console.error('Failed to toggle star', err);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        },
      }}
    >
      <CardActionArea onClick={() => navigate(`/recipes/${recipe.id}`)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Cover Image */}
        <Box
          sx={{
            height: 160,
            background: recipe.coverImage ? `url(${recipe.coverImage}) center/cover` : gradient,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {/* Status badge */}
          <Chip
            label={recipe.status === 'made' ? '✅ Made It' : '📌 Bookmarked'}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              bgcolor: 'rgba(0,0,0,0.65)',
              color: '#fff',
              fontSize: '0.7rem',
              height: 22,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Category emoji */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'rgba(0,0,0,0.6)',
              borderRadius: 1,
              px: 1,
              py: 0.25,
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography sx={{ fontSize: '0.85rem' }}>{category?.emoji}</Typography>
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>
              {category?.label}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1 }}
            >
              {recipe.title}
            </Typography>
            <IconButton
              size="small"
              onClick={handleStarToggle}
              sx={{ color: recipe.starred ? 'warning.main' : 'text.secondary', mt: -0.5, mr: -0.5, flexShrink: 0 }}
            >
              {recipe.starred ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
            </IconButton>
          </Box>

          {/* Tags */}
          {recipe.tags?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {recipe.tags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(255,255,255,0.07)' }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1 }}>
            {/* Time + servings */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {totalTime > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {totalTime}m
                  </Typography>
                </Box>
              )}
              {recipe.servings && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <People sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {recipe.servings}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Difficulty */}
            {difficulty && (
              <Chip
                label={difficulty.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  bgcolor: `${difficulty.color}20`,
                  color: difficulty.color,
                  border: `1px solid ${difficulty.color}40`,
                }}
              />
            )}
          </Box>

          {/* Rating */}
          {recipe.status === 'made' && recipe.rating && (
            <Rating value={recipe.rating} size="small" readOnly sx={{ mt: 0.5 }} />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
